// Cover décors as ICONS — shared helpers.
//
// The covers catalogue's transparent décor PNGs (cover.design_url, public
// bucket) double as the icon set: choosable on the blank ("vierge") cover and
// placeable as page elements. Serving is the proven Supabase image-transform
// pattern (see pickerThumbUrl): alpha-preserving WebP/AVIF, ~1.7MB → tens of
// KB, CDN + browser cached — icons add no editor weight. Full-res design_url
// stays the print source.
//
// The destination matcher is the SAME tolerant scorer the cover picker's
// auto-match uses (extracted from AlbumFlowPage) so "icons for my destination
// float to the top" behaves identically everywhere.

import type { Cover } from './api';
import { DECOR_BASE } from './stickers/catalog';

/** Square, contain-fit, light transform of a public storage URL — for icon
 *  grids. Non-Supabase / already-transformed URLs pass through unchanged. */
export function iconThumbUrl(url: string | null | undefined, size = 200): string | null | undefined {
  if (!url) return url;
  if (url.includes('/render/image/')) return url;
  const marker = '/storage/v1/object/public/';
  const i = url.indexOf(marker);
  if (i === -1) return url;
  const rendered = `${url.slice(0, i)}/storage/v1/render/image/public/${url.slice(i + marker.length)}`;
  const sep = rendered.includes('?') ? '&' : '?';
  return `${rendered}${sep}width=${size}&height=${size}&resize=contain&quality=70`;
}

// ── Destination/event matching (same rules as the cover picker auto-match) ──
export const normDest = (s: string) => s.toLowerCase().trim().normalize('NFD').replace(/[̀-ͯ]/g, '');
const compactDest = (s: string) => normDest(s).replace(/[^a-z0-9]/g, '');

/** Higher score = stronger match against the typed destination. 0 = unrelated. */
export function destinationMatchScore(cover: Cover, dest: string): number {
  if (!dest || dest.length < 2) return 0;
  const dN = normDest(dest), dC = compactDest(dest);
  if (dC.length < 2) return 0;
  const nameN = normDest(cover.name), nameC = compactDest(cover.name);
  const tags = (cover.search_tags ?? []).map(normDest);
  const cats = cover.categories.map(normDest);
  if (nameC === dC) return 100;                                        // exact name
  if (tags.includes(dN)) return 92;                                    // exact tag
  if (nameC.includes(dC) || dC.includes(nameC)) return 85;            // contains (space/punct-tolerant)
  if (tags.some((t) => t.includes(dN) || dN.includes(t))) return 70;  // partial tag
  const dTokens = dN.split(/\s+/).filter((w) => w.length >= 3);
  const nameTokens = new Set(nameN.split(/\s+/).filter((w) => w.length >= 3));
  const overlap = dTokens.filter((w) => nameTokens.has(w)).length;
  if (overlap > 0) return 40 + overlap * 5;                            // shared significant words
  if (cats.some((c) => c === dN || dN.includes(c))) return 25;        // same category (weak)
  return 0;
}

/** Best light source for an icon TILE: the pre-generated trimmed WebP asset
 *  (icon_url, ~tens of KB) when the batch script has run, else an on-the-fly
 *  CDN transform of the full design. */
export function iconSrc(cover: Pick<Cover, 'design_url' | 'icon_url'>, size = 200): string | undefined {
  return cover.icon_url ?? iconThumbUrl(cover.design_url, size) ?? undefined;
}

/** What the editor CANVAS draws for an image icon: the sticker's own lighter
 *  copy (`previewUrl`, a file — no transform) when it has one, else a 400 px
 *  CDN transform of the print file. The PDF always embeds `imageUrl`. */
export function iconDisplayUrl(el: { imageUrl?: string; previewUrl?: string }): string | undefined {
  return el.previewUrl || (iconThumbUrl(el.imageUrl, 400) ?? undefined) || el.imageUrl;
}

/**
 * An « Icônes » TILE (2026-10-14). Before: each tile loaded the ≤ 800 px icon
 * file (~64 KB, 13 MB for the 253 covers, all at once — native lazy-loading
 * measures the window, not the panel's scroll box) or a cold CDN transform:
 * tiles stayed blank on a phone. A white artwork (doves, a maple leaf) was
 * invisible on the cream tile. Now: a 200 px WebP made by
 * scripts/themes/build_sticker_catalog.py (decor/thumbs/covers/<path>.webp,
 * ~9 KB) when the list says it exists, the old source as the fallback (a cover
 * added since), and `bg` — the tile colour for an artwork too light for cream.
 */
export function iconTile(
  cover: Pick<Cover, 'design_url' | 'icon_url'>,
  thumbs: Record<string, string> | null | undefined,
): { src: string | undefined; fallback: string | undefined; bg: string | null } {
  const fallback = iconSrc(cover, 200);
  const file = cover.icon_url ?? cover.design_url ?? '';
  const marker = '/storage/v1/object/public/covers/';
  const at = file.indexOf(marker);
  const key = at === -1 ? '' : file.slice(at + marker.length);
  if (!thumbs || !key || !(key in thumbs)) return { src: fallback, fallback, bg: null };
  return {
    src: `${DECOR_BASE}thumbs/covers/${key.replace(/\.[A-Za-z0-9]+$/, '')}.webp`,
    fallback,
    bg: thumbs[key] || null,
  };
}

/** Visible covers that have a transparent décor (the icon set). */
export function iconCovers(covers: Cover[]): Cover[] {
  return covers.filter((c) => c.is_visible !== false && !!c.design_url);
}

/** Icon set ordered for the client: destination/event matches FIRST (score
 *  desc), the rest keep their incoming (popularity) order. Also returns how
 *  many lead entries are matches so grids can label the "for you" section. */
export function sortIconsByDestination(covers: Cover[], destination: string): { list: Cover[]; matchCount: number } {
  const icons = iconCovers(covers);
  if (!destination || normDest(destination).length < 2) return { list: icons, matchCount: 0 };
  const scored = icons.map((c, i) => ({ c, i, score: destinationMatchScore(c, destination) }));
  const matches = scored.filter((s) => s.score > 0).sort((a, b) => b.score - a.score || a.i - b.i);
  if (matches.length === 0) return { list: icons, matchCount: 0 };
  const matchIds = new Set(matches.map((m) => m.c.id));
  return {
    list: [...matches.map((m) => m.c), ...icons.filter((c) => !matchIds.has(c.id))],
    matchCount: matches.length,
  };
}
